import {CheckIcon, ChevronDownIcon} from "@heroicons/react/16/solid";
import {Dropdown, DropdownButton, DropdownHeader, DropdownItem, DropdownLabel, DropdownMenu} from "catalyst";
import {OtsTransportCategoryIcon} from "../components/OtsTransportCategoryIcon";

export type OtsTransportCategoryOption = {
  /** WATER, OIL, … */
  code: string;
  label: string;
};

export type OtsTransportCategorySwitcherProps = {
  options: OtsTransportCategoryOption[];
  /** Code of the category the app is working in. */
  value: string;
  onChange: (code: string) => void;
  /** A shorter button for beside the logo, with its menu opening to the right; it keeps the standard width. */
  compact?: boolean;
};

/** The button wears the commodity's own color, so which one the app is in is never in doubt. */
const buttonColors = (code: string) => code.toUpperCase() === "OIL"
  ? "bg-[var(--ots-transport-category-oil-fill,#475569)] text-white hover:bg-slate-500"
  : code.toUpperCase() === "WATER"
    ? "bg-[#1c7ed6] text-white hover:bg-[#1971c2]"
    : "bg-zinc-600 text-white hover:bg-zinc-500";

/**
 * A menu row lights up in its commodity's color, the same one the button wears once it's chosen; anything else
 * takes the theme's primary, as the region and account menus do. The droplet turns white to stay visible on it.
 */
const optionHighlight = (code: string) => [
  code.toUpperCase() === "OIL"
    ? "data-focus:!bg-[var(--ots-transport-category-oil-fill,#475569)] data-focus:!text-white"
    : code.toUpperCase() === "WATER"
      ? "data-focus:!bg-[#1c7ed6] data-focus:!text-white"
      : "data-focus:!bg-[var(--ots-primary)] data-focus:!text-[var(--ots-on-primary)]",
  "data-focus:[&_svg]:!text-white",
].join(" ");

/** Switches the commodity the whole app works in. A catalyst dropdown, to sit in a catalyst navbar. */
export const OtsTransportCategorySwitcher = ({options, value, onChange, compact = false}: OtsTransportCategorySwitcherProps) => {
  const current = options.find(option => option.code === value);

  return <Dropdown>
    <DropdownButton
      as="button"
      type="button"
      aria-label={`Category: ${current?.label ?? value}. Change category`}
      data-category={value.toUpperCase()}
      className={[
        "inline-flex items-center justify-center rounded-lg font-semibold shadow-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-zinc-950",
        compact ? "h-7 min-w-24 gap-1.5 px-2.5 text-xs" : "h-9 min-w-24 gap-1.5 px-3 text-sm",
        buttonColors(value),
      ].join(" ")}>
      {/* White on the commodity's own fill: the droplet marks the category without fighting the button's color. */}
      <OtsTransportCategoryIcon category={value} className={["!text-white/90", compact ? "size-3.5" : "size-4"].join(" ")}/>
      {current?.label ?? value}
      <ChevronDownIcon aria-hidden="true" className={compact ? "size-3.5 opacity-80" : "size-4 opacity-80"}/>
    </DropdownButton>
    <DropdownMenu anchor={compact ? "bottom start" : "bottom end"} className="z-[1001] min-w-44">
      <DropdownHeader><span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">Category</span></DropdownHeader>
      {options.map(option => <DropdownItem key={option.code} className={optionHighlight(option.code)} onClick={() => option.code !== value && onChange(option.code)}>
        <span data-slot="icon" className="inline-flex"><OtsTransportCategoryIcon category={option.code} className="size-full"/></span>
        <DropdownLabel>{option.label}</DropdownLabel>
        {option.code === value && <CheckIcon aria-hidden="true" className="col-start-5 size-4 justify-self-end"/>}
      </DropdownItem>)}
    </DropdownMenu>
  </Dropdown>;
};
